import { CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { Controller, useForm } from 'libs/form'
import { format } from 'libs/dates'
import { routes } from 'config/routes'
import { useUserRoles } from 'shared/hooks'
import { BackButton, DatePicker, Form, FormSection, Header, Switch } from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { useFetchOpeningHour } from './queries/useFetchOpeningHour'
import { usePopulateOpeningHour } from './hooks/usePopulateOpeningHour'
import { useEditOpeningHourMutation } from './mutations/useEditOpeningHourMutation'

const OpeningHourPage = () => {
	const { t } = useTranslation()
	const { isUser } = useUserRoles()

	const {
		handleSubmit,
		register,
		control,
		reset,
		formState: { errors }
	} = useForm({
		mode: 'onChange'
	})

	const { openingHour } = useFetchOpeningHour()

	usePopulateOpeningHour({
		data: openingHour?.data,
		reset
	})

	const { editOpeningHourMutation, isEditMutationLoading } = useEditOpeningHourMutation()

	const onSubmit = (data: any) => {
		editOpeningHourMutation({
			...data,
			date: format(data.date, 'yyyy-MM-dd')
		})
	}

	const flags = [
		{
			name: 'is_open',
			label: t('Open'),
			description: t('The store is open at the selected date.')
		},
		{
			name: 'is_bookable',
			label: t('Bookable'),
			description: t('The selected day can be selected as the first day of a new booking')
		},
		{
			name: 'counts_towards_duration',
			label: t('Counts towards duration'),
			description: t(
				'The day counts towards the length of a booking. Turn it off for days with shorter opening hours, such as December 24th.'
			)
		}
	]

	return (
		<>
			<Header
				title={t('Edit opening hours')}
				eyebrow={t('Booking engine')}
				actions={<BackButton to={routes.openingHourIndex} text={t('Back to opening hours')} />}
			/>
			<div className="px-5 lg:px-0">
				<Form
					variant="sections"
					submitText={t('Save changes')}
					cancelText={t('Discard changes')}
					onCancel={() => reset()}
					onSubmit={handleSubmit(onSubmit)}
					isLoading={isEditMutationLoading}
					disabled={isUser}>
					<FormSection
						title={t('Day')}
						description={t('The date and an optional note about it.')}
						icon={<CalendarDaysIcon />}>
						<Controller
							control={control}
							name="date"
							rules={{ required: t('This field is required') }}
							render={({ field, fieldState }) => (
								<DatePicker
									label={t('Date')}
									selected={field.value}
									required
									disabled={isUser}
									error={fieldState.error}
									onChange={field.onChange}
								/>
							)}
						/>
						<InputGroup
							id="reason"
							label={t('Reason (optional)')}
							placeholder={t('e.g. Christmas Eve')}
							disabled={isUser}
							{...register('reason')}
							error={(errors as any).reason}
						/>
					</FormSection>
					<FormSection
						title={t('Availability')}
						description={t('How the day works in the online booking widget.')}
						icon={<ClockIcon />}
						tone="success"
						className="sm:grid-cols-1">
						{flags.map((flag) => (
							<Switch
								key={flag.name}
								label={flag.label}
								description={flag.description}
								disabled={isUser}
								{...register(flag.name)}
							/>
						))}
					</FormSection>
				</Form>
			</div>
		</>
	)
}

export default OpeningHourPage
